import {
  DropdownFilters,
  defaultDropdownFilter
} from '@hooks/useAssessmentDropdownFilters'
import { useQuery } from '@tanstack/react-query'
import { GetAPIparams, getTitle } from '@utils/assessmentReportUtils'
import { ToGradeTextString } from '@utils/getGradeFromId'
import { Col, Flex, Row, Spin } from 'antd'

import { Entities_Assessment_PlayerSummary_Response } from '@suntex.fim/api-client'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import { useFramework } from '@suntex.fim/framework'
import { useOptionalPlayerContext } from '@suntex.fim/player'
import { useOptionalTeamInfo } from '@suntex.fim/team'

function AssessmentReportSummaryDashboard({
  dropdownFilters = defaultDropdownFilter,
  isSchool = false,
  isPlayer = false,
  player
}: {
  dropdownFilters?: DropdownFilters
  isSchool?: boolean
  isPlayer?: boolean
  player?: Entities_Assessment_PlayerSummary_Response
}) {
  const { api } = useFramework()
  const team = useOptionalTeamInfo()
  const { login } = useAuth(isAuthenticated)
  const playerTeam = useOptionalPlayerContext()?.playerInfo ?? null
  const { id, year, grade, level } = GetAPIparams(
    isSchool,
    dropdownFilters,
    isPlayer
  )

  const { data: summaryResponse, isLoading } = useQuery({
    queryKey: [
      'assessment',
      'entitySummaryByAggregateLevel',
      id,
      level,
      year,
      grade
    ],
    queryFn: () =>
      api.get_Assessment_EntitySummaryByAggregateLevel({
        entityId: id!,
        level,
        year: year!,
        grade
      }),
    enabled: id != null
  })

  const teacherName = summaryResponse?.contactName || ''
  const summaryRecordData = summaryResponse?.entitySummaries?.[0]
  const getName = () => {
    if (isSchool) {
      return summaryRecordData?.name || login?.schoolName
    }
    if (isPlayer) {
      return player?.userId
    }
    return team?.name ?? playerTeam?.team?.teamName
  }
  const getGrade = () => {
    if (isSchool) {
      return summaryRecordData?.teamCount.toLocaleString() || 0
    }
    if (isPlayer) {
      return player?.grade
    }
    return ToGradeTextString(team?.grade ?? playerTeam?.team?.grade)
  }
  const getTeamTeacher = () => {
    if (isSchool) {
      return (
        <>
          <span className="label">Stickers per Student</span>
          <span className="numbers">
            {summaryRecordData?.stickersPerStudent.toLocaleString() || 0}
          </span>
        </>
      )
    }
    if (isPlayer) {
      return (
        <Flex gap={10}>
          <>
            <span className="label">Team</span>
            <span className="numbers">{player?.teamName}</span>
          </>
        </Flex>
      )
    }
    return (
      <>
        <span className="label">Teacher</span>
        <span className="numbers">
          {teacherName.length > 0
            ? teacherName
            : playerTeam?.team?.teamLeaderContact}
        </span>
      </>
    )
  }
  const getPlayerSticker = () => {
    if (isPlayer) {
      return (
        <>
          <span className="label">Stickers</span>
          <span className="numbers">
            {player?.stickerCount.toLocaleString()}
          </span>
        </>
      )
    }
    return null
  }
  return (
    <Flex
      className={`main-dashboard ${isLoading ? 'loading-back' : ''}`}
      vertical
    >
      <Flex>
        <h4 className="title">{`${getTitle(isSchool, isPlayer)} Summary`}</h4>
      </Flex>
      <Row>
        {isLoading ? (
          <Flex flex="auto" justify="center" align="center">
            <Spin />
          </Flex>
        ) : (
          <>
            <Col
              xs={{ flex: '100%' }}
              sm={{ flex: '50%' }}
              md={{ flex: '33.33%' }}
              xl={{ flex: '0 0 280px' }}
              xxl={{ flex: '0 0 25%' }}
              className="stat"
            >
              <span className="label">{`${getTitle(isSchool, isPlayer)} Name`}</span>
              <span className="numbers">{getName()}</span>
            </Col>
            <Col
              xs={{ flex: '100%' }}
              sm={{ flex: '50%' }}
              md={{ flex: '33.33%' }}
              xl={{ flex: '0 0 280px' }}
              xxl={{ flex: '0 0 25%' }}
              className="stat"
            >
              <span className="label">{isSchool ? 'Team Count' : 'Grade'}</span>
              <span className="numbers">{getGrade()}</span>
            </Col>
            <Col
              xs={{ flex: '100%' }}
              sm={{ flex: '50%' }}
              md={{ flex: '33.33%' }}
              xl={{ flex: '0 0 280px' }}
              xxl={{ flex: '0 0 25%' }}
              className="stat"
            >
              {getTeamTeacher()}
            </Col>
            {isPlayer && (
              <Col
                xs={{ flex: '100%' }}
                sm={{ flex: '50%' }}
                md={{ flex: '33.33%' }}
                xl={{ flex: '0 0 280px' }}
                xxl={{ flex: '0 0 25%' }}
                className="stat"
              >
                {getPlayerSticker()}
              </Col>
            )}
            <Col
              xs={{ flex: '100%' }}
              sm={{ flex: '50%' }}
              md={{ flex: '33.33%' }}
              xl={{ flex: '0 0 280px' }}
              xxl={{ flex: '0 0 25%' }}
              className="stat"
            >
              <span className="label">
                {isSchool ? 'Total Licenses Purchased' : 'School Year'}
              </span>
              <span className="numbers">
                {isSchool
                  ? summaryRecordData?.totalLicenses.toLocaleString() || 0
                  : team?.year || login?.year}
              </span>
            </Col>
            {!isPlayer && (
              <>
                <Col
                  xs={{ flex: '100%' }}
                  sm={{ flex: '50%' }}
                  md={{ flex: '33.33%' }}
                  xl={{ flex: '0 0 280px' }}
                  xxl={{ flex: '0 0 25%' }}
                  className="stat"
                >
                  <span className="label">
                    {isSchool ? 'Stickers per Team' : 'Stickers'}
                  </span>
                  <span className="numbers">
                    {isSchool
                      ? summaryRecordData?.stickersPerTeam.toLocaleString() || 0
                      : (summaryRecordData?.stickers.toLocaleString() ??
                        (playerTeam?.team?.stickers.toLocaleString() ||
                          0 ||
                          0))}
                  </span>
                </Col>
                <Col
                  xs={{ flex: '100%' }}
                  sm={{ flex: '50%' }}
                  md={{ flex: '33.33%' }}
                  xl={{ flex: '0 0 280px' }}
                  xxl={{ flex: '0 0 25%' }}
                  className="stat"
                >
                  <span className="label">
                    {isSchool
                      ? 'Total Licenses Activated'
                      : 'Avg. Stickers per Student'}
                  </span>
                  <span className="numbers">
                    {isSchool
                      ? summaryRecordData?.students.toLocaleString() || 0
                      : (summaryRecordData?.stickersPerStudent.toLocaleString() ??
                        (playerTeam?.team?.averageStickers.toLocaleString() ||
                          0))}
                  </span>
                </Col>
              </>
            )}
            {isSchool && (
              <Col
                xs={{ flex: '100%' }}
                sm={{ flex: '50%' }}
                md={{ flex: '33.33%' }}
                xl={{ flex: '0 0 280px' }}
                xxl={{ flex: '0 0 25%' }}
                className="stat"
              >
                <span className="label">Stickers</span>
                <span className="numbers">
                  {isSchool
                    ? summaryRecordData?.stickers.toLocaleString() || 0
                    : summaryRecordData?.stickersPerStudent.toLocaleString() ||
                      0}
                </span>
              </Col>
            )}
          </>
        )}
      </Row>
    </Flex>
  )
}

export default AssessmentReportSummaryDashboard
